import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormInputState, FormModel, ResultValue } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import { validateRequire } from 'src/api/utils/formValidators';
import { CustomSelectionsType, CustomSelectionType } from './commonCmsGuard';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { CustomSelectionsTemplateState } from 'src/AppState/CmsState/EventDetailsState/CustomSelectionsTemplate/CustomSelectionsTemplateState';
import { CustomSelectionStateNew } from './CustomSelectionStateNew';
import { TypeId } from 'src_common/common/websocket2/type';

interface SelectOptionsType {
    key: string;
    name: string;
}

export class MarketWithCustomSelectionsState {
    private readonly customSelectionMapNew: MobxMapAutoNew<string, MobxMapAutoNew<number, CustomSelectionStateNew>>;

    @observable isOpenAccordion: boolean = false;

    readonly selectionTemplateModel: FormModel<string>;
    readonly selectionTemplate: FormInputState<string>;
    readonly selectionTemplateStatus: InputStatus;

    constructor(
        private readonly modelsState: ModelsState,
        private readonly customSelectionsTemplateState: CustomSelectionsTemplateState,
        private readonly marketId: TypeId.MarketId,
        private readonly handleDeleteSelectedMarket: (id: TypeId.MarketId) => void
    ) {
        this.customSelectionMapNew = new MobxMapAutoNew(
            (selectionTemplate: string): MobxMapAutoNew<number, CustomSelectionStateNew> =>
                new MobxMapAutoNew(
                    (selectionId: number): CustomSelectionStateNew =>
                        new CustomSelectionStateNew(
                            this.modelsState,
                            this.customSelectionsTemplateState,
                            this.marketId,
                            selectionId,
                            selectionTemplate
                        )
                )
        );

        this.selectionTemplate = new FormInputState('', false);
        this.selectionTemplateModel = this.selectionTemplate.map(validateRequire);
        this.selectionTemplateStatus = new InputStatus(this.selectionTemplateModel);
        makeAutoObservable(this);
    }

    public getCustomSelectionModelByIdNew = (id: number): CustomSelectionStateNew | null => {
        if (this.selectionTemplateDataNew === '') {
            return null;
        }
        return this.customSelectionMapNew.get(this.selectionTemplateDataNew).get(id);
    };

    @computed private get marketModel(): MarketModel | null {
        return this.modelsState.getMarket(this.marketId);
    }

    @computed public get marketName(): string {
        return this.marketModel?.name ?? '';
    }

    @computed private get selections(): Array<SelectionModel> {
        return this.marketModel?.selections ?? [];
    }

    @computed public get selectionsIds(): Array<number> {
        return this.selections.map((selection) => selection.id);
    }

    @computed public get selectionTemplateOptions(): Array<SelectOptionsType> {
        const selectionTemplateOptionsList = this.customSelectionsTemplateState.customSelectionsTemplateList.map(
            (elem) => {
                return {
                    key: elem.slug,
                    name: elem.label,
                };
            }
        );

        const defaultItem = {
            key: '',
            name: 'Select template...',
        };

        return [defaultItem, ...selectionTemplateOptionsList];
    }

    @computed public get selectionTemplateDataNew(): string {
        const selectionTemplate = this.selectionTemplateModel.result;
        if (selectionTemplate instanceof ResultValue) {
            return selectionTemplate.value;
        }

        return '';
    }

    @computed public get selectionsData(): Array<CustomSelectionType> {
        const selections: Array<CustomSelectionType> = [];
        for (const selectionId of this.selectionsIds) {
            const selectionModel = this.getCustomSelectionModelByIdNew(selectionId);
            if (selectionModel !== null && selectionModel.isValidForm && selectionModel.selectionData !== null) {
                selections.push(selectionModel.selectionData);
            }
        }
        return selections;
    }

    @action public onToggleAccordion = (): void => {
        this.isOpenAccordion = !this.isOpenAccordion;
    };

    @action public onDeleteSelectedMarket = (): void => {
        this.handleDeleteSelectedMarket(this.marketId);
    };

    @action public setCustomSelections = (customSelection: CustomSelectionsType): void => {
        const selectionIds: Array<number> = [];
        this.selectionTemplate.setValue(customSelection.selection_type);
        for (const selection of customSelection.selections) {
            selectionIds.push(selection.id);
            const selectionModel = this.customSelectionMapNew.get(customSelection.selection_type).get(selection.id);
            selectionModel.setSelectionForm(selection);
        }
    };
}
